import _objectSpread from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread";
import _toConsumableArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import addSnackbarToModel from '@jbrowse/core/ui/SnackbarModel';
import { getContainingView } from '@jbrowse/core/util';
import { readConfObject } from '@jbrowse/core/configuration';
import { version } from '../version';
import { cast, getMembers, getParent, getSnapshot, getType, isAlive, isModelType, isReferenceType, types, walk } from 'mobx-state-tree';
import InfoIcon from '@mui/icons-material/Info';
import AboutDialog from '@jbrowse/core/ui/AboutDialog';
/**
 * #stateModel JBrowseReactLGVSessionModel
 * inherits SnackbarModel
 */
export default function sessionModelFactory(pluginManager) {
  var model = types.model('ReactLinearGenomeViewSession', {
    /**
     * #property
     */
    name: types.identifier,
    /**
     * #property
     */
    margin: 0,
    /**
     * #property
     */
    view: pluginManager.getViewType('LinearGenomeView').stateModel,
    /**
     * #property
     */
    widgets: types.map(pluginManager.pluggableMstType('widget', 'stateModel')),
    /**
     * #property
     */
    activeWidgets: types.map(types.safeReference(pluginManager.pluggableMstType('widget', 'stateModel'))),
    /**
     * #property
     */
    connectionInstances: types.array(pluginManager.pluggableMstType('connection', 'stateModel')),
    /**
     * #property
     */
    sessionTracks: types.array(pluginManager.pluggableConfigSchemaType('track'))
  }).volatile(function /* self */
  () {
    return {
      /**
       * this is the globally "selected" object. can be anything.
       * code that wants to deal with this should examine it to see what
       * kind of thing it is.
       */
      selection: undefined,
      /**
       * this is the current "task" that is being performed in the UI.
       * this is usually an object of the form
       * `{ taskName: "configure", target: thing_being_configured }`
       */
      task: undefined,
      queueOfDialogs: []
    };
  }).views(function (self) {
    return {
      /**
       * #getter
       */
      get disableAddTracks() {
        return getParent(self).disableAddTracks;
      },
      /**
       * #getter
       */
      get DialogComponent() {
        var _a;
        if (self.queueOfDialogs.length) {
          return (_a = self.queueOfDialogs[0]) === null || _a === void 0 ? void 0 : _a[0];
        }
        return undefined;
      },
      /**
       * #getter
       */
      get DialogProps() {
        var _a;
        if (self.queueOfDialogs.length) {
          return (_a = self.queueOfDialogs[0]) === null || _a === void 0 ? void 0 : _a[1];
        }
        return undefined;
      },
      /**
       * #getter
       */
      get textSearchManager() {
        return getParent(self).textSearchManager;
      },
      /**
       * #getter
       */
      get rpcManager() {
        return getParent(self).rpcManager;
      },
      /**
       * #getter
       */
      get configuration() {
        return getParent(self).config.configuration;
      },
      /**
       * #getter
       */
      get assemblies() {
        return [getParent(self).config.assembly];
      },
      /**
       * #getter
       */
      get assemblyNames() {
        return [getParent(self).config.assemblyName];
      },
      /**
       * #getter
       */
      get tracks() {
        return getParent(self).config.tracks;
      },
      /**
       * #getter
       */
      get aggregateTextSearchAdapters() {
        return getParent(self).config.aggregateTextSearchAdapters;
      },
      /**
       * #getter
       */
      get connections() {
        return getParent(self).config.connections;
      },
      /**
       * #getter
       */
      get adminMode() {
        return false;
      },
      /**
       * #getter
       */
      get assemblyManager() {
        return getParent(self).assemblyManager;
      },
      /**
       * #getter
       */
      get version() {
        return version;
      },
      /**
       * #getter
       */
      get views() {
        return [self.view];
      },
      /**
       * #method
       */
      renderProps: function renderProps() {
        return {
          theme: readConfObject(this.configuration, 'theme')
        };
      },
      /**
       * #getter
       */
      get visibleWidget() {
        if (isAlive(self)) {
          // returns most recently added item in active widgets
          return Array.from(self.activeWidgets.values())[self.activeWidgets.size - 1];
        }
        return undefined;
      },
      /**
       * #method
       * See if any MST nodes currently have a types.reference to this object.
       * @param object - object
       * @returns An array where the first element is the node referring
       * to the object and the second element is they property name the node is
       * using to refer to the object
       */
      getReferring: function getReferring(object) {
        var refs = [];
        walk(getParent(self), function (node) {
          if (isModelType(getType(node))) {
            var members = getMembers(node);
            Object.entries(members.properties).forEach(function (_ref) {
              var _ref2 = _slicedToArray(_ref, 2),
                key = _ref2[0],
                value = _ref2[1];
              // @ts-ignore
              if (isReferenceType(value) && node[key] === object) {
                refs.push({
                  node: node,
                  key: key
                });
              }
            });
          }
        });
        return refs;
      }
    };
  }).actions(function (self) {
    return {
      /**
       * #action
       */
      addTrackConf: function addTrackConf(trackConf) {
        var trackId = trackConf.trackId,
          type = trackConf.type;
        if (!type) {
          throw new Error("unknown track type ".concat(type));
        }
        var track = self.sessionTracks.find(function (t) {
          return t.trackId === trackId;
        });
        if (track) {
          return track;
        }
        var length = self.sessionTracks.push(trackConf);
        return self.sessionTracks[length - 1];
      },
      /**
       * #action
       */
      queueDialog: function queueDialog(callback) {
        var _this = this;
        var _callback = callback(function () {
            _this.removeActiveDialog();
          }),
          _callback2 = _slicedToArray(_callback, 2),
          component = _callback2[0],
          props = _callback2[1];
        self.queueOfDialogs = [].concat(_toConsumableArray(self.queueOfDialogs), [[component, props]]);
      },
      /**
       * #action
       */
      removeActiveDialog: function removeActiveDialog() {
        self.queueOfDialogs = self.queueOfDialogs.slice(1);
      },
      /**
       * #action
       */
      makeConnection: function makeConnection(configuration) {
        var initialSnapshot = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        var type = configuration.type;
        if (!type) {
          throw new Error('track configuration has no `type` listed');
        }
        var name = readConfObject(configuration, 'name');
        var connectionType = pluginManager.getConnectionType(type);
        if (!connectionType) {
          throw new Error("unknown connection type ".concat(type));
        }
        var connectionData = _objectSpread({}, initialSnapshot, {
          name: name,
          type: type,
          configuration: configuration
        });
        var length = self.connectionInstances.push(connectionData);
        return self.connectionInstances[length - 1];
      },
      /**
       * #action
       */
      removeReferring: function removeReferring(referring, track, callbacks, dereferenceTypeCount) {
        var _this2 = this;
        referring.forEach(function (_ref3) {
          var node = _ref3.node;
          var dereferenced = false;
          try {
            // If a view is referring to the track config, remove the track
            // from the view
            var type = 'open track(s)';
            var view = getContainingView(node);
            callbacks.push(function () {
              return view.hideTrack(track.trackId);
            });
            dereferenced = true;
            if (!dereferenceTypeCount[type]) {
              dereferenceTypeCount[type] = 0;
            }
            dereferenceTypeCount[type] += 1;
          } catch (err1) {
            // ignore
          }
          if (_this2.hasWidget(node)) {
            // If a configuration editor widget has the track config
            // open, close the widget
            var _type = 'configuration editor widget(s)';
            callbacks.push(function () {
              return _this2.hideWidget(node);
            });
            dereferenced = true;
            if (!dereferenceTypeCount[_type]) {
              dereferenceTypeCount[_type] = 0;
            }
            dereferenceTypeCount[_type] += 1;
          }
          if (!dereferenced) {
            throw new Error("Error when closing this connection, the following node is still referring to a track configuration: ".concat(JSON.stringify(getSnapshot(node))));
          }
        });
      },
      /**
       * #action
       */
      prepareToBreakConnection: function prepareToBreakConnection(configuration) {
        var _this3 = this;
        var callbacksToDereferenceTrack = [];
        var dereferenceTypeCount = {};
        var name = readConfObject(configuration, 'name');
        var connection = self.connectionInstances.find(function (c) {
          return c.name === name;
        });
        connection.tracks.forEach(function (track) {
          var referring = self.getReferring(track);
          _this3.removeReferring(referring, track, callbacksToDereferenceTrack, dereferenceTypeCount);
        });
        var safelyBreakConnection = function safelyBreakConnection() {
          callbacksToDereferenceTrack.forEach(function (cb) {
            return cb();
          });
          _this3.breakConnection(configuration);
        };
        return [safelyBreakConnection, dereferenceTypeCount];
      },
      /**
       * #action
       */
      breakConnection: function breakConnection(configuration) {
        var name = readConfObject(configuration, 'name');
        var connection = self.connectionInstances.find(function (c) {
          return c.name === name;
        });
        self.connectionInstances.remove(connection);
      },
      /**
       * #action
       */
      addView: function addView(typeName) {
        var initialState = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        var typeDefinition = pluginManager.getElementType('view', typeName);
        if (!typeDefinition) {
          throw new Error("unknown view type ".concat(typeName));
        }
        self.view = cast(_objectSpread({}, initialState, {
          type: typeName
        }));
        return self.view;
      },
      removeView: function removeView() {},
      /**
       * #action
       */
      addWidget: function addWidget(typeName, id) {
        var initialState = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
        var configuration = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {
          type: typeName
        };
        var typeDefinition = pluginManager.getElementType('widget', typeName);
        if (!typeDefinition) {
          throw new Error("unknown widget type ".concat(typeName));
        }
        var data = _objectSpread({}, initialState, {
          id: id,
          type: typeName,
          configuration: configuration
        });
        self.widgets.set(id, data);
        return self.widgets.get(id);
      },
      /**
       * #action
       */
      showWidget: function showWidget(widget) {
        if (self.activeWidgets.has(widget.id)) {
          self.activeWidgets.delete(widget.id);
        }
        self.activeWidgets.set(widget.id, widget);
      },
      /**
       * #action
       */
      hasWidget: function hasWidget(widget) {
        return self.activeWidgets.has(widget.id);
      },
      /**
       * #action
       */
      hideWidget: function hideWidget(widget) {
        self.activeWidgets.delete(widget.id);
      },
      /**
       * #action
       */
      hideAllWidgets: function hideAllWidgets() {
        self.activeWidgets.clear();
      },
      /**
       * #action
       * set the global selection, i.e. the globally-selected object.
       * can be a feature, a view, just about anything
       * @param thing -
       */
      setSelection: function setSelection(thing) {
        self.selection = thing;
      },
      /**
       * #action
       * clears the global selection
       */
      clearSelection: function clearSelection() {
        self.selection = undefined;
      },
      /**
       * #action
       */
      clearConnections: function clearConnections() {
        self.connectionInstances.length = 0;
      },
      /**
       * #action
       */
      renameCurrentSession: function renameCurrentSession(sessionName) {
        return getParent(self).renameCurrentSession(sessionName);
      }
    };
  }).views(function (self) {
    return {
      /**
       * #method
       */
      getTrackActionMenuItems: function getTrackActionMenuItems(config) {
        return [{
          label: 'About track',
          onClick: function onClick() {
            self.queueDialog(function (doneCallback) {
              return [AboutDialog, {
                config: config,
                handleClose: doneCallback
              }];
            });
          },
          icon: InfoIcon
        }];
      }
    };
  });
  return addSnackbarToModel(model);
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function z(x) {
  // this function's sole purpose is to get typescript to check
  // that the session model implements all of AbstractSessionModel
  return x;
}